import { Trans, useTranslation } from 'react-i18next';

import {
    Anchor,
    Caption,
    FilledCard,
    Flex,
    Heading1,
    Heading2,
    Heading3,
    RichText,
    Stack,
} from '@myphx-engineering/phx-design-system';

import type { PathwayOptionConfig } from '../../../../configuration/shared';
import { useFillPrescription } from '../../../../contexts/fill/use-fill-prescription';
import { useCardPricing } from '../../../../hooks/use-card-pricing';
import { useDrugKey } from '../../../../hooks/use-drug-key';
import { useProgramTerms } from '../../../../hooks/use-program-terms';
import { getAbsoluteRoute } from '../../../../util/get-absolute-route';
import { ConditionalComponent } from '../../../utility/ConditionalComponent';

interface MedicareBridgeEligibilityCardProps {
    option: PathwayOptionConfig;
}

export const MedicareBridgeEligibilityCard = ({
    option,
}: MedicareBridgeEligibilityCardProps) => {
    const { t } = useTranslation('lilly');
    const { medication } = useFillPrescription();
    const drugKey = useDrugKey(medication);
    const terms = useProgramTerms(medication, option.fillPathway);
    const { displayPrice, offer } = useCardPricing(option);

    const resolvedPrice = displayPrice ?? offer?.patientCost;
    const cardColor = option.content.cardColor ?? 'gray';

    const shortDescBody = terms?.savingsCard?.shortDesc?.body ?? null;
    const superscript = option.content.priceSuperscript;

    const outcomeKey =
        option.fillPathway === 'government' ? 'bridge' : 'self-pay';
    const hasTermsContent =
        !!option.content.priceNote || (!!shortDescBody && !!drugKey);

    return (
        <Stack gap="xs">
            <FilledCard
                color={cardColor}
                data-testid={`pharmacy-select-${outcomeKey}-pricing-card`}
            >
                <Stack gap="xs">
                    <Heading3>{option.content.heading}</Heading3>

                    {resolvedPrice != null && option.content.showPrice && (
                        <Flex align="baseline" gap={0}>
                            <Heading1>${resolvedPrice}</Heading1>
                            <Heading2>
                                {t('fill.pathSelect.cards.perMonth')}
                                <sup>{superscript}</sup>
                            </Heading2>
                        </Flex>
                    )}
                </Stack>
            </FilledCard>

            {hasTermsContent && (
                <Stack
                    gap="xs"
                    data-testid={`pharmacy-select-${outcomeKey}-terms-and-conditions`}
                >
                    <RichText text={option.content.priceNote ?? ''} />

                    <ConditionalComponent
                        condition={
                            !option.content.priceNote &&
                            !!shortDescBody &&
                            !!drugKey
                        }
                    >
                        <Caption>
                            <Trans
                                ns="dynamic"
                                defaults={`‡${shortDescBody}`}
                                components={{
                                    1: (
                                        <Anchor
                                            href={
                                                terms?.termsUrl ??
                                                getAbsoluteRoute(
                                                    'programs.drugKey.terms.pathway',
                                                    {
                                                        drugKey: drugKey!,
                                                        pathway:
                                                            option.fillPathway,
                                                    }
                                                )
                                            }
                                            target="_blank"
                                        />
                                    ),
                                }}
                            />
                        </Caption>
                    </ConditionalComponent>
                </Stack>
            )}
        </Stack>
    );
};
